import { useParams } from "react-router-dom";
import { useMemo } from "react";
import {
  useAssessmentEntriesQuery,
  useAssessmentReportQuery,
  useStudentQuery,
} from "@/hooks/useStudents";
import { useCatalog } from "@/providers/CatalogProvider";
import { formatDate } from "@/utils/date";
import { getDomainByCode } from "@/constants/domains";
import type { DomainCode } from "@/constants/domains";
import { EChart } from "@/components/charts/EChart";
import { ReportBreadcrumbs } from "@/components/report/ReportBreadcrumbs";
import { ReportExportPanel } from "@/components/report/ReportExportPanel";
import { FloatingReportLayout } from "@/components/report/FloatingReportLayout";
import { ReportSourceError } from "@/components/report/ReportSourceError";
import {
  PrintableRadarChart,
  PrintableReportDocument,
  PrintableReportHeader,
  PrintableReportSection,
} from "@/components/report/PrintableReport";
import { assessmentBelongsToStudent, assessmentRecordLabel } from "@/utils/student-workspace";
import { printReport } from "@/utils/report-print";
import type { AssessmentEntry, Student } from "@/types/assessment";
import type { ScaleLibrary } from "@/types/scale";
import type { ActivityLibrary } from "@/types/activity";
import type { AssessmentReport } from "@/services/repositories/assessment-repository";

export function ReportDetailPage(): JSX.Element {
  const { studentId = "", assessmentId } = useParams<{
    studentId: string;
    assessmentId: string;
  }>();
  const reportQuery = useAssessmentReportQuery(assessmentId);
  const studentQuery = useStudentQuery(studentId);
  const { scaleLibrary, activityLibrary } = useCatalog();
  const assessment = reportQuery.data?.assessment;
  const contextMatches = assessmentBelongsToStudent(assessment, studentId);
  const entriesQuery = useAssessmentEntriesQuery(contextMatches ? assessmentId : undefined);

  const domainScores = useMemo(() => {
    return (reportQuery.data?.summary.domains ?? []).map((score) => ({
      domain: score.code as DomainCode,
      average: score.average,
      completed: score.assessedCount,
      total: score.assessedCount,
    }));
  }, [reportQuery.data]);

  const subdomainScores = useMemo(() => {
    return (reportQuery.data?.summary.subdomains ?? []).map((score) => ({
      key: score.key,
      average: score.average,
      completed: score.assessedCount,
      total: score.assessedCount,
    }));
  }, [reportQuery.data]);

  const radarOption = useMemo(() => {
    if (domainScores.length === 0) {
      return undefined;
    }
    const indicator = domainScores.map((score) => ({
      name: getDomainByCode(score.domain).name,
      max: 2,
    }));
    const values = domainScores.map((score) => score.average ?? 0);
    return {
      title: { text: "领域得分雷达图", left: "center", textStyle: { fontSize: 16 } },
      tooltip: {},
      radar: {
        indicator,
        radius: "60%",
      },
      series: [
        {
          type: "radar",
          areaStyle: { opacity: 0.2 },
          data: [
            {
              value: values,
              name: "平均得分",
            },
          ],
        },
      ],
    };
  }, [domainScores]);

  const barOption = useMemo(() => {
    if (domainScores.length === 0) {
      return undefined;
    }
    const categories = domainScores.map((score) => getDomainByCode(score.domain).name);
    const values = domainScores.map((score) => Number(score.average?.toFixed(2) ?? 0));
    return {
      title: { text: "领域得分柱状图", left: "center", textStyle: { fontSize: 16 } },
      tooltip: { trigger: "axis" },
      xAxis: { type: "category", data: categories },
      yAxis: { type: "value", min: 0, max: 2 },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: { color: "#527a2a" },
        },
      ],
    };
  }, [domainScores]);

  const reportTocItems = useMemo(
    () => [
      { id: "report-basic", label: "基本信息" },
      { id: "report-domain-overview", label: "领域得分" },
      ...(subdomainScores.length > 0 ? [{ id: "report-subdomains", label: "子领域明细" }] : []),
      ...((reportQuery.data?.activityObservations?.length ?? 0) > 0
        ? [{ id: "report-observations", label: "活动观察" }]
        : []),
      { id: "report-items", label: "条目明细" },
    ],
    [reportQuery.data?.activityObservations?.length, subdomainScores.length],
  );

  const handleDownloadPdf = () => {
    printReport(studentQuery.data?.profile.name ?? "儿童", "单次评估报告");
  };

  if (!reportQuery.isLoading && assessment && !contextMatches) {
    return (
      <ReportSourceError
        studentId={studentId}
        title="儿童与评估报告不匹配"
        description="这份报告不属于当前儿童。为保护数据，页面不会显示报告内容。"
      />
    );
  }

  return (
    <>
      <div className="space-y-6 print:hidden">
        <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <ReportBreadcrumbs currentPage="单次评估报告" studentId={studentId} />
            <h1 className="text-2xl font-semibold text-slate-800">单次评估报告</h1>
          </div>
          <ReportExportPanel onDownloadPdf={handleDownloadPdf} disabled={reportQuery.isLoading} />
        </header>

        <FloatingReportLayout items={reportTocItems}>
          <section
            id="report-basic"
            className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-slate-700">基本信息</h2>
            {reportQuery.isLoading ? (
              <p className="mt-2 text-sm text-slate-500">正在加载评估信息…</p>
            ) : null}
            {reportQuery.isError ? (
              <p className="mt-2 text-sm text-red-500">评估信息加载失败，请稍后重试。</p>
            ) : null}
            {assessment ? (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <InfoRow label="儿童" value={studentQuery.data?.profile.name ?? "当前儿童"} />
                <InfoRow label="评估类型" value={assessmentRecordLabel()} />
                <InfoRow label="评估时间" value={formatDate(assessment.startedAt)} />
                <InfoRow label="评估师" value={assessment.evaluatorDisplayName ?? "历史评估师"} />
                <InfoRow label="状态" value={renderAssessmentStatus(assessment.status)} />
                <InfoRow
                  label="提交时间"
                  value={assessment.submittedAt ? formatDate(assessment.submittedAt) : "—"}
                />
              </div>
            ) : null}
          </section>

          <section
            id="report-domain-overview"
            className="scroll-mt-[11.5rem] space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-slate-700">领域得分概览</h2>
            {domainScores.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">暂无得分数据。</p>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <EChart option={radarOption} />
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <EChart option={barOption} />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-slate-500">
                        <th className="px-4 py-2 font-medium">领域</th>
                        <th className="px-4 py-2 font-medium">平均得分</th>
                        <th className="px-4 py-2 font-medium">完成条目</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
                      {domainScores.map((score) => {
                        const domainMeta = getDomainByCode(score.domain);
                        return (
                          <tr key={score.domain}>
                            <td className="px-4 py-3">{domainMeta.name}</td>
                            <td className="px-4 py-3">{score.average ?? "-"}</td>
                            <td className="px-4 py-3">
                              {score.completed} / {score.total}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {subdomainScores.length > 0 ? (
            <section
              id="report-subdomains"
              className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-700">子领域得分明细</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-slate-500">
                      <th className="px-4 py-2 font-medium">子领域</th>
                      <th className="px-4 py-2 font-medium">领域</th>
                      <th className="px-4 py-2 font-medium">平均得分</th>
                      <th className="px-4 py-2 font-medium">完成条目</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
                    {subdomainScores.map((score) => {
                      const [domainCode, subCode] = score.key.split("-");
                      const domain = getDomainByCode(domainCode as DomainCode);
                      const subdomain = scaleLibrary.domains
                        .find((item) => item.domainCode === domainCode)
                        ?.subdomains.find((item) => item.subdomainCode === subCode);
                      return (
                        <tr key={score.key}>
                          <td className="px-4 py-3">{subdomain?.subdomainLabel ?? score.key}</td>
                          <td className="px-4 py-3">{domain.name}</td>
                          <td className="px-4 py-3">{score.average ?? "-"}</td>
                          <td className="px-4 py-3">
                            {score.completed} / {score.total}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {(reportQuery.data?.activityObservations?.length ?? 0) > 0 ? (
            <section
              id="report-observations"
              className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-700">活动观察记录</h2>
              <div className="mt-4 space-y-3">
                {reportQuery.data?.activityObservations?.map((observation) => {
                  const activity = activityLibrary.activityIndex.get(observation.activityCode);
                  const step = activity?.modules
                    .flatMap((module) => module.steps)
                    .find((item) => item.id === observation.stepId);
                  return (
                    <article
                      key={`${observation.activityCode}:${observation.stepId}`}
                      className="rounded-lg border border-brand/20 bg-brand/5 p-4"
                    >
                      <p className="text-sm font-semibold text-slate-800">
                        {activity?.name ?? observation.activityCode}
                        {step ? ` · ${step.title}` : ""}
                      </p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                        {observation.text}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        {observation.updatedByDisplayName ?? "历史评估师"} ·{" "}
                        {formatDate(observation.updatedAt)}
                      </p>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section
            id="report-items"
            className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-slate-700">条目明细</h2>
            {entriesQuery.isLoading ? (
              <p className="mt-2 text-sm text-slate-500">正在加载条目得分…</p>
            ) : null}
            {entriesQuery.isError ? (
              <p className="mt-2 text-sm text-red-500">条目数据加载失败，请稍后重试。</p>
            ) : null}
            {entriesQuery.data && entriesQuery.data.length > 0 ? (
              <div className="mt-4 space-y-3">
                {entriesQuery.data.map((entry) => {
                  const catalogEntry = scaleLibrary.entryIndex.get(entry.entryCode);
                  return (
                    <div
                      key={entry.entryCode}
                      className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
                    >
                      <p className="font-semibold text-slate-700">
                        {entry.entryCode} · {catalogEntry?.longTermGoal.title ?? "未匹配的量表条目"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        得分：{entry.score ?? "未评分"} · 最近更新：{formatDate(entry.updatedAt)}
                      </p>
                      {entry.comment ? (
                        <p className="mt-2 text-xs text-slate-500">备注：{entry.comment}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {entriesQuery.data && entriesQuery.data.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">目前没有条目数据。</p>
            ) : null}
          </section>
        </FloatingReportLayout>
      </div>
      {reportQuery.data && contextMatches ? (
        <ReportDetailPrintableDocument
          report={reportQuery.data}
          student={studentQuery.data}
          entries={entriesQuery.data ?? []}
          domainScores={domainScores}
          subdomainScores={subdomainScores}
          scaleLibrary={scaleLibrary}
          activityLibrary={activityLibrary}
        />
      ) : null}
    </>
  );
}

function ReportDetailPrintableDocument({
  report,
  student,
  entries,
  domainScores,
  subdomainScores,
  scaleLibrary,
  activityLibrary,
}: {
  report: AssessmentReport;
  student: Student | undefined;
  entries: AssessmentEntry[];
  domainScores: Array<{
    domain: DomainCode;
    average: number | null;
    completed: number;
    total: number;
  }>;
  subdomainScores: Array<{
    key: string;
    average: number | null;
    completed: number;
    total: number;
  }>;
  scaleLibrary: ScaleLibrary;
  activityLibrary: ActivityLibrary;
}): JSX.Element {
  const { assessment } = report;

  return (
    <PrintableReportDocument ariaLabel="单次评估报告打印文档">
      <PrintableReportHeader
        eyebrow="评估结果"
        title="单次评估报告"
        description="本次评估的领域得分、观察记录与条目明细"
        student={student}
        assessmentLabel={`${formatDate(assessment.startedAt)} · ${assessmentRecordLabel()}`}
      />

      <PrintableReportSection title="一、评估信息">
        <div className="report-print-block overflow-hidden rounded-xl border border-slate-200">
          <table className="report-print-table">
            <tbody>
              <tr>
                <th>评估类型</th>
                <td>{assessmentRecordLabel()}</td>
                <th>评估师</th>
                <td>{assessment.evaluatorDisplayName ?? "历史评估师"}</td>
              </tr>
              <tr>
                <th>评估状态</th>
                <td>{renderAssessmentStatus(assessment.status)}</td>
                <th>提交时间</th>
                <td>{assessment.submittedAt ? formatDate(assessment.submittedAt) : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </PrintableReportSection>

      <PrintableReportSection
        title="二、领域得分概览"
        subtitle="各领域已评分条目的平均分，满分为 2 分"
      >
        {domainScores.length > 0 ? (
          <>
            <div className="report-print-chart report-print-block">
              <PrintableRadarChart
                ariaLabel="领域得分雷达图"
                labels={domainScores.map((score) =>
                  getDomainByCode(score.domain).name.replace("领域", ""),
                )}
                values={domainScores.map((score) => score.average ?? 0)}
                maxValue={2}
              />
            </div>
            <div className="report-print-block mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="report-print-table">
                <thead>
                  <tr>
                    <th>领域</th>
                    <th>平均得分</th>
                    <th>完成条目</th>
                  </tr>
                </thead>
                <tbody>
                  {domainScores.map((score) => (
                    <tr key={score.domain}>
                      <td className="font-semibold">{getDomainByCode(score.domain).name}</td>
                      <td>{score.average ?? "—"}</td>
                      <td>
                        {score.completed} / {score.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="report-print-prose">暂无得分数据。</p>
        )}
      </PrintableReportSection>

      {subdomainScores.length > 0 ? (
        <PrintableReportSection title="三、子领域得分明细">
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="report-print-table">
              <thead>
                <tr>
                  <th>子领域</th>
                  <th>领域</th>
                  <th>平均得分</th>
                  <th>完成条目</th>
                </tr>
              </thead>
              <tbody>
                {subdomainScores.map((score) => {
                  const [domainCode, subCode] = score.key.split("-");
                  const subdomain = scaleLibrary.domains
                    .find((item) => item.domainCode === domainCode)
                    ?.subdomains.find((item) => item.subdomainCode === subCode);
                  return (
                    <tr key={score.key}>
                      <td className="font-semibold">{subdomain?.subdomainLabel ?? score.key}</td>
                      <td>{getDomainByCode(domainCode as DomainCode).name}</td>
                      <td>{score.average ?? "—"}</td>
                      <td>
                        {score.completed} / {score.total}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PrintableReportSection>
      ) : null}

      {(report.activityObservations?.length ?? 0) > 0 ? (
        <PrintableReportSection title="四、活动观察记录">
          <div className="space-y-3">
            {report.activityObservations?.map((observation) => {
              const activity = activityLibrary.activityIndex.get(observation.activityCode);
              const step = activity?.modules
                .flatMap((module) => module.steps)
                .find((item) => item.id === observation.stepId);
              return (
                <article
                  key={`${observation.activityCode}:${observation.stepId}`}
                  className="report-print-block rounded-xl border border-brand/20 bg-brand/5 p-4"
                >
                  <h3 className="text-[10.5pt] font-semibold text-slate-800">
                    {activity?.name ?? observation.activityCode}
                    {step ? ` · ${step.title}` : ""}
                  </h3>
                  <p className="mt-2 whitespace-pre-wrap text-[10pt] leading-6 text-slate-700">
                    {observation.text}
                  </p>
                  <p className="mt-2 text-[8.5pt] text-slate-500">
                    {observation.updatedByDisplayName ?? "历史评估师"} ·{" "}
                    {formatDate(observation.updatedAt)}
                  </p>
                </article>
              );
            })}
          </div>
        </PrintableReportSection>
      ) : null}

      <PrintableReportSection title="五、条目明细">
        {entries.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="report-print-table report-print-table-compact">
              <thead>
                <tr>
                  <th>条目</th>
                  <th>得分</th>
                  <th>最近更新</th>
                  <th>备注</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const catalogEntry = scaleLibrary.entryIndex.get(entry.entryCode);
                  return (
                    <tr key={entry.entryCode}>
                      <td>
                        {entry.entryCode} · {catalogEntry?.longTermGoal.title ?? "未匹配的量表条目"}
                      </td>
                      <td>{entry.score ?? "未评分"}</td>
                      <td>{formatDate(entry.updatedAt)}</td>
                      <td>{entry.comment || "\u00a0"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="report-print-prose">目前没有条目数据。</p>
        )}
      </PrintableReportSection>
    </PrintableReportDocument>
  );
}

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-medium text-slate-700">{value}</p>
    </div>
  );
}

function renderAssessmentStatus(status: string): string {
  return status === "completed" ? "已完成" : "进行中";
}
